import { DIFYMessageChunk } from '@/pages/ISWAN_CHAT/interface';
import { formatThoughtMsg } from '@/pages/ISWAN_CHAT/utils/formatThoughtMsg';
import { safeParse } from '@/pages/ISWAN_CHAT/utils/safeParse';

interface DecodedResponseChunkBase {
  type: 'message' | 'node' | 'ping' | 'error';
  conversationId: string;
  taskId: string;
  end?: boolean;
}

export interface DecodedResponseChunkMessage extends DecodedResponseChunkBase {
  type: 'message';
  text: string;
}

export interface DecodedResponseChunkNode extends DecodedResponseChunkBase {
  type: 'node';
  originType: DIFYMessageChunk['event'];
  nodeId?: string;
  status: 'pending' | 'success' | 'error';
  title: string;
  description?: string;
  content?: string;
}

export interface DecodedResponseChunkPing extends DecodedResponseChunkBase {
  type: 'ping';
  originType: 'ping';
}

export interface DecodedResponseChunkError extends DecodedResponseChunkBase {
  type: 'error';
}

export type DecodedResponseChunk =
  | DecodedResponseChunkMessage
  | DecodedResponseChunkNode
  | DecodedResponseChunkPing
  | DecodedResponseChunkError;

function unescapeJsonString(str: string) {
  // 第一步：处理反斜杠转义（如 \\ 转义为 \）
  let unescaped = str.replace(/\\\\/g, '\\');

  // 第二步：处理其他转义字符
  unescaped = unescaped.replace(/\\"/g, '"'); // 双引号
  // .replace(/\\n/g, '\n')    // 换行
  // .replace(/\\r/g, '\r')     // 回车
  // .replace(/\\t/g, '\t')     // 制表符
  // .replace(/\\b/g, '\b')     // 退格
  // .replace(/\\f/g, '\f')     // 换页
  // .replace(/\\\//g, '/');    // 斜杠

  // 第三步：处理Unicode转义（如 \u0020 -> 空格）
  unescaped = unescaped.replace(/\\u([\dA-Fa-f]{4})/g, (_match, grp) =>
    String.fromCharCode(parseInt(grp, 16)),
  );

  return unescaped;
}

const decodeEvent__message = (chunkData: DIFYMessageChunk): DecodedResponseChunkMessage => {
  const _chunkData = { ...chunkData };

  _chunkData.answer = formatThoughtMsg(_chunkData.answer);

  return {
    type: 'message',
    text: _chunkData.answer,
    conversationId: _chunkData.conversation_id,
    taskId: _chunkData.task_id,
  };
};

const decodeEvent__message_end = (chunkData: DIFYMessageChunk): DecodedResponseChunkMessage => {
  return {
    end: true,
    type: 'message',
    text: '',
    conversationId: chunkData.conversation_id,
    taskId: chunkData.task_id,
  };
};

const decodeEvent__workflow_started = (chunkData: DIFYMessageChunk): DecodedResponseChunkNode => {
  return {
    type: 'node',
    originType: chunkData.event,
    // workflow 节点特殊，不是连续的两条，这里先固定为success
    status: 'success',
    title: '工作流初始化',
    description: '用户提问：' + chunkData?.data?.inputs?.['sys.query'],
    conversationId: chunkData.conversation_id,
    taskId: chunkData.task_id,
  };
};

const decodeEvent__workflow_finished = (chunkData: DIFYMessageChunk): DecodedResponseChunkNode => {
  return {
    type: 'node',
    originType: chunkData.event,
    status: 'success',
    title: '工作流结束',
    conversationId: chunkData.conversation_id,
    content: chunkData?.data?.outputs?.answer,
    taskId: chunkData.task_id,
  };
};

const decodeEvent__node_started = (chunkData: DIFYMessageChunk): DecodedResponseChunkNode => {
  let title = chunkData?.data?.title ?? '启动工作节点';
  let status: DecodedResponseChunkNode['status'] = 'pending';

  if (chunkData?.data?.node_type === 'agent') {
    title = 'Agent开始接管工作...';
    status = 'success';
  }

  return {
    type: 'node',
    originType: chunkData.event,
    nodeId: chunkData?.data?.node_id,
    conversationId: chunkData.conversation_id,
    taskId: chunkData.task_id,
    status,
    title,
  };
};

const decodeEvent__node_finished = (chunkData: DIFYMessageChunk): DecodedResponseChunkNode => {
  let title = chunkData?.data?.title ?? '工作节点完成';
  let description = undefined;
  let content = undefined;

  if (chunkData?.data?.node_type === 'knowledge-retrieval') {
    description = '共搜索到 ' + chunkData?.data?.outputs?.result?.length + ' 篇知识';
    content = chunkData?.data?.outputs?.result
      ?.map((e: any) => '### ' + e.title + '\n' + e.content)
      ?.join('\n');
  }

  if (chunkData?.data?.node_type === 'agent') {
    title = 'Agent已完成当前任务，正在整理结果...';
  }

  return {
    type: 'node',
    originType: chunkData.event,
    nodeId: chunkData?.data?.node_id,
    status: 'success',
    conversationId: chunkData.conversation_id,
    taskId: chunkData.task_id,
    title,
    description,
    content,
  };
};

const decodeEvent__agent_log = (chunkData: DIFYMessageChunk): DecodedResponseChunkNode => {
  let title = chunkData?.data?.label ?? '工作节点';
  let description = undefined;
  let content = undefined;

  if (chunkData?.data?.data?.thought !== undefined) {
    content = formatThoughtMsg(chunkData?.data?.data?.thought, true);
  }

  if (chunkData?.data?.data?.output !== undefined) {
    content += chunkData?.data?.data?.output;
  }

  if (chunkData?.data?.data?.observation !== undefined) {
    content += chunkData?.data?.data?.observation;
  }

  return {
    type: 'node',
    originType: chunkData.event,
    nodeId: chunkData?.data?.node_id,
    status: 'success',
    conversationId: chunkData.conversation_id,
    taskId: chunkData.task_id,
    title,
    description,
    content,
  };
};

const decodeEventPing = (): DecodedResponseChunkPing => {
  return {
    end: false,
    type: 'ping',
    originType: 'ping',
    conversationId: '',
    taskId: '',
  };
};

const decodeEventFailBack = (chunkData: DIFYMessageChunk | null): DecodedResponseChunkError => {
  return {
    end: true,
    type: 'error',
    conversationId: chunkData?.conversation_id ?? '',
    taskId: chunkData?.task_id ?? '',
  };
};

export const decodeResponseChunk = (chunkStr: string): DecodedResponseChunk => {
  // 转换这一大段回复的内容answer
  const _text = unescapeJsonString(chunkStr.trim());
  const chunkData = safeParse<DIFYMessageChunk>(_text);

  switch (chunkData?.event) {
    case 'message':
      return decodeEvent__message(chunkData);
    case 'message_end':
      return decodeEvent__message_end(chunkData);
    case 'workflow_started':
      return decodeEvent__workflow_started(chunkData);
    case 'workflow_finished':
      return decodeEvent__workflow_finished(chunkData);
    case 'node_started':
      return decodeEvent__node_started(chunkData);
    case 'node_finished':
      return decodeEvent__node_finished(chunkData);
    case 'agent_log':
      return decodeEvent__agent_log(chunkData);
    case 'error':
      return decodeEventFailBack(chunkData);
    default: {
      if (chunkStr === 'event: ping') {
        return decodeEventPing();
      }
      return decodeEventFailBack(chunkData);
    }
  }
};

export const decodeResponseChunkList = (
  strList: string[],
): { chunkList: DecodedResponseChunk[]; conversationId: string } => {
  let chunkList: DecodedResponseChunk[] = [];
  let cId = '';
  for (let i = 0; i < strList.length; i++) {
    const chunk = decodeResponseChunk(strList[i]);
    chunkList.push(chunk);
    if (!!chunk.conversationId) {
      cId = chunk.conversationId;
    }
  }
  return { chunkList, conversationId: cId };
};
